/**
 * Time-attack ghost playback: a recorded lap trajectory (flat [x, y, angle]
 * triplets, uniform in time from start line to finish line) interpolated
 * against the local lap clock. Purely visual — the ghost has no collision, no
 * server presence, and loops onto its next lap when it beats you to the line.
 */

import type { GhostData } from '@microligue/shared';
import { lerpAngle } from '@microligue/shared';

export interface GhostPose {
  x: number;
  y: number;
  angle: number;
}

/**
 * Pose of the ghost `elapsedMs` after the start line. Loops past its lap time
 * (the ghost starts its next lap — a rolling pace car). Null when the data is
 * too short to interpolate.
 */
export function ghostPoseAt(ghost: GhostData, elapsedMs: number): GhostPose | null {
  const count = Math.floor(ghost.samples.length / 3);
  if (count < 2 || ghost.timeMs <= 0) return null;
  const t = ((elapsedMs % ghost.timeMs) + ghost.timeMs) % ghost.timeMs;
  const f = (t / ghost.timeMs) * (count - 1);
  const i = Math.min(Math.floor(f), count - 2);
  const a = f - i;
  const s = ghost.samples;
  const x0 = s[i * 3] as number;
  const y0 = s[i * 3 + 1] as number;
  const g0 = s[i * 3 + 2] as number;
  const x1 = s[(i + 1) * 3] as number;
  const y1 = s[(i + 1) * 3 + 1] as number;
  const g1 = s[(i + 1) * 3 + 2] as number;
  return {
    x: x0 + (x1 - x0) * a,
    y: y0 + (y1 - y0) * a,
    angle: lerpAngle(g0, g1, a),
  };
}

/** Pose of the ghost parked on the start line (lap not armed yet). */
export function ghostStartPose(ghost: GhostData): GhostPose | null {
  const count = Math.floor(ghost.samples.length / 3);
  if (count < 1) return null;
  return {
    x: ghost.samples[0] as number,
    y: ghost.samples[1] as number,
    angle: ghost.samples[2] as number,
  };
}
